/* Prestige Watch — pw-motion.js
   Site-wide Motion (motiondivision/motion UMD) choreography.
   Safety contract:
   - Content is fully visible without JS; the pw-motion-on class stages reveals
     only after the Motion lib is verified (progressive enhancement).
   - Wall-clock failsafes force-show: 2.8s above-the-fold, 9s everything.
   - hidden tab / blur / resize / reduced-motion flips restore everything.
   - Nothing inside sticky/header chrome is staged. */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var M = window.Motion;
  if (reduce || !M || typeof M.animate !== 'function') return;

  var doc = document.documentElement;
  var animate = M.animate, inView = M.inView;
  var staged = []; /* {el, reset} */
  var pending = []; /* pre-staged elements awaiting their reveal */

  var SKIP = '.pw-header, .pw-announcement, .pw-cart-mechanics, .progress-wrap, .pw-mobile-nav, [class*=sticky], .jdgm, .chaty-app-outer-forms';

  function allowed(el) {
    if (!el || !el.closest) return false;
    if (el.closest(SKIP)) return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 || r.height > 0;
  }

  function record(el, reset) { staged.push({ el: el, reset: reset }); }

  function forceShow(el) {
    el.style.removeProperty('opacity');
    el.style.removeProperty('transform');
    el.style.removeProperty('clip-path');
    if (el.classList.contains('pw-m-word')) el.classList.remove('pw-m-word');
  }

  function showAll() {
    doc.classList.remove('pw-motion-on');
    staged.forEach(function (s) {
      try { if (s.anim) s.anim.stop(); } catch (e) {}
      s.reset();
    });
    staged.length = 0;
    Array.prototype.forEach.call(document.querySelectorAll('.pw-m-word > span'), forceShow);
    Array.prototype.forEach.call(document.querySelectorAll('.pw-m-word'), forceShow);
    pending.splice(0).forEach(function (rec) { if (rec && rec.el) forceShow(rec.el); });
    Array.prototype.forEach.call(document.querySelectorAll('[data-pw-count]'), function (el) {
      el.textContent = el.getAttribute('data-pw-count');
    });
    var bar = document.getElementById('pw-progress');
    if (bar) bar.style.opacity = '0';
  }

  /* Watchdog: anything pre-staged, still hidden, and inside the viewport gets
     force-shown — covers reveals whose inView never fired (hidden-then-shown
     containers, fast scroll jumps, throttled IntersectionObserver). */
  function sweep() {
    var now = performance.now();
    for (var i = pending.length - 1; i >= 0; i--) {
      var rec = pending[i];
      var el = rec && rec.el;
      if (!el || !el.style) { pending.splice(i, 1); continue; }
      if (el.style.opacity !== '0' && !el.style.transform) { pending.splice(i, 1); continue; }
      var r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) { pending.splice(i, 1); continue; }
      /* at or above the fold line (in view OR already scrolled past), OR waited too long */
      var reached = r.top < window.innerHeight * 1.02;
      var expired = now - rec.at > 6000;
      if (reached || expired) {
        forceShow(el);
        pending.splice(i, 1);
      }
    }
  }

  /* Stage = add class + set start state in the same tick (no flash). */
  doc.classList.add('pw-motion-on');

  function stageStart(el, keyframes) {
    if (keyframes.opacity && keyframes.opacity[0] === 0) el.style.opacity = '0';
    var t = [];
    var unit = function (v) { return typeof v === 'number' ? v + 'px' : v; };
    if (keyframes.y) t.push('translateY(' + unit(keyframes.y[0]) + ')');
    if (keyframes.x) t.push('translateX(' + unit(keyframes.x[0]) + ')');
    if (keyframes.scale) t.push('scale(' + unit(keyframes.scale[0]) + ')');
    if (keyframes.scaleX) t.push('scaleX(' + unit(keyframes.scaleX[0]) + ')');
    if (t.length) el.style.transform = t.join(' ');
    pending.push({ el: el, at: performance.now() });
  }

  function unstage(el) {
    for (var i = pending.length - 1; i >= 0; i--) {
      if (pending[i] && pending[i].el === el) pending.splice(i, 1);
    }
  }

  function run(el, keyframes, options, resetStyles) {
    if (!allowed(el)) return null;
    /* Inline start state (same tick) — avoids CSS staging so completion reset
       returns the element to its natural visible state. */
    stageStart(el, keyframes);
    var reset = function () {
      (resetStyles || ['opacity', 'transform']).forEach(function (p) { el.style.removeProperty(p); });
    };
    var anim;
    try {
      anim = animate(el, keyframes, options);
    } catch (e) { reset(); return null; }
    var rec = { el: el, reset: reset, anim: anim };
    staged.push(rec);
    unstage(el);
    Promise.resolve(anim).then(function () {
      reset();
      var i = staged.indexOf(rec); if (i >= 0) staged.splice(i, 1);
    }, reset);
    return anim;
  }

  /* ---------- word split (headlines) ---------- */
  function splitWords(el) {
    if (!el || el.getAttribute('data-pw-split')) return [];
    el.setAttribute('data-pw-split', '1');
    el.setAttribute('aria-label', (el.textContent || '').trim());
    var out = [];
    function wrap(node) {
      var frag = document.createDocumentFragment();
      node.textContent.split(/(\s+)/).forEach(function (part) {
        if (!part) return;
        if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(' ')); return; }
        var w = document.createElement('span'); w.className = 'pw-m-word'; w.setAttribute('aria-hidden', 'true');
        var inner = document.createElement('span'); inner.textContent = part;
        w.appendChild(inner); frag.appendChild(w); out.push(w);
      });
      node.parentNode.replaceChild(frag, node);
    }
    Array.prototype.slice.call(el.childNodes).forEach(function (n) {
      if (n.nodeType === 3 && n.textContent.trim()) wrap(n);
      else if (n.nodeType === 1 && !n.classList.contains('pw-m-word')) {
        Array.prototype.slice.call(n.childNodes).forEach(function (m) {
          if (m.nodeType === 3 && m.textContent.trim()) wrap(m);
        });
      }
    });
    return out;
  }

  function animateWords(words, baseDelay, duration) {
    words.forEach(function (w, i) {
      var inner = w.firstElementChild;
      run(inner, { y: ['112%', '0%'], opacity: [0, 1] }, {
        duration: duration || 0.85,
        delay: baseDelay + i * 0.055,
        easing: [0.2, 0.8, 0.2, 1]
      });
    });
  }

  /* ---------- 1. hero / page-title entrance (above the fold) ---------- */
  var hero = document.querySelector('.pw-hero');
  if (hero) {
    var h1 = hero.querySelector('h1');
    var words = splitWords(h1);
    animateWords(words, 0.1, 0.9);
    var seq = hero.querySelectorAll('.pw-eyebrow, .pw-lead, .pw-btn-row, .pw-actions, .pw-hero-trust, .pw-breadcrumbs, p');
    Array.prototype.forEach.call(seq, function (el, i) {
      run(el, { y: [18, 0], opacity: [0, 1] }, { duration: 0.7, delay: 0.25 + i * 0.09, easing: [0.22, 1, 0.36, 1] });
    });
    var art = hero.querySelector('.pw-hero-editorial, .pw-hero-grid img, .pw-hero-grid picture, .pw-dial');
    if (art) run(art, { opacity: [0, 1], scale: [0.94, 1] }, { duration: 1.1, delay: 0.35, easing: [0.2, 0.8, 0.2, 1] }, ['opacity', 'transform']);
    var rules = hero.querySelectorAll('.pw-rule, hr');
    Array.prototype.forEach.call(rules, function (el) {
      run(el, { scaleX: [0, 1], opacity: [0, 1] }, { duration: 0.9, delay: 0.5, easing: [0.22, 1, 0.36, 1] });
    });
  } else {
    var pageTitle = document.querySelector('main h1, .pw-hero--page h1, .pw-hero--collection h1, h1');
    animateWords(splitWords(pageTitle), 0.05, 0.8);
  }

  /* ---------- 2. scroll reveals with staggered groups ---------- */
  var groups = [
    ['.pw-watch-grid', '.pw-watch-card, .product-card', 0.06],
    ['.pw-brand-grid', '.pw-brand-card', 0.08],
    ['.pw-brand-links', 'a', 0.05],
    ['.pw-footer-top', 'nav, div, section', 0.09],
    ['.pw-actions', 'a, button', 0.08],
    ['.product-grid', 'li', 0.05],
    ['.pw-outlets', '.pw-outlet, article, div', 0.08],
    ['.pw-page-content', ':scope > *', 0.07],
    ['.pw-product-details, .product-single__description, .rte', ':scope > *', 0.06],
    ['.pw-blog-list, .blog-list', '.blog__item, article', 0.07]
  ];
  var tagged = new Set();
  groups.forEach(function (g) {
    Array.prototype.forEach.call(document.querySelectorAll(g[0]), function (container) {
      if (!allowed(container) || container.closest(SKIP)) return;
      var items = Array.prototype.filter.call(container.querySelectorAll(g[1]), function (el) {
        return !tagged.has(el) && allowed(el);
      });
      if (!items.length) return;
      items.forEach(function (el) { tagged.add(el); });
      var kf = { y: [22, 0], opacity: [0, 1] };
      var fire = function () {
        items.forEach(function (el, i) {
          run(el, { y: kf.y, opacity: kf.opacity }, {
            duration: 0.65,
            delay: i * g[2],
            easing: [0.22, 1, 0.36, 1]
          });
        });
      };
      var rect = container.getBoundingClientRect();
      if (rect.top > window.innerHeight * 0.9 && typeof inView === 'function') {
        /* below the fold: stage hidden now, reveal on scroll */
        items.forEach(function (el) { stageStart(el, kf); });
        inView(container, function () { fire(); return function () {}; });
      } else {
        fire();
      }
    });
  });

  /* Section headings: word-rise on first view */
  Array.prototype.forEach.call(document.querySelectorAll('.pw-page-content h2, .pw-page-content h3, .pw-footer h2, main h2'), function (h) {
    if (h.closest(SKIP) || tagged.has(h)) return;
    var ws = splitWords(h);
    if (!ws.length) return;
    var wkf = { y: ['112%', '0%'], opacity: [0, 1] };
    if (typeof inView === 'function') {
      ws.forEach(function (w) { if (w.firstElementChild) stageStart(w.firstElementChild, wkf); });
      inView(h, function () {
        animateWords(ws, 0, 0.75);
        return function () {};
      });
    } else {
      animateWords(ws, 0, 0.75);
    }
  });

  /* Individual leftovers: images, rules, paragraphs outside known groups */
  Array.prototype.forEach.call(document.querySelectorAll('main img, .pw-page-content img, article img'), function (img) {
    if (tagged.has(img) || img.closest(SKIP) || !allowed(img)) return;
    tagged.add(img);
    if (typeof inView === 'function') {
      stageStart(img, { opacity: [0, 1], y: [16, 0] });
      inView(img, function () {
        run(img, { opacity: [0, 1], y: [16, 0] }, { duration: 0.8, easing: [0.22, 1, 0.36, 1] });
        return function () {};
      });
    }
  });
  Array.prototype.forEach.call(document.querySelectorAll('main hr, .pw-page-content hr'), function (el) {
    if (tagged.has(el) || el.closest(SKIP)) return;
    tagged.add(el);
    if (typeof inView === 'function') {
      stageStart(el, { scaleX: [0, 1], opacity: [0, 1] });
      inView(el, function () {
        run(el, { scaleX: [0, 1], opacity: [0, 1] }, { duration: 0.8, easing: [0.22, 1, 0.36, 1] });
        return function () {};
      });
    }
  });

  /* ---------- 3. counters ---------- */
  Array.prototype.forEach.call(document.querySelectorAll('[data-pw-count]'), function (el) {
    var target = parseInt(el.getAttribute('data-pw-count'), 10);
    if (isNaN(target)) return;
    el.textContent = '0';
    var fire = function () {
      var t0 = performance.now(), dur = 1400;
      (function tick(now) {
        var p = Math.min(1, (now - t0) / dur);
        el.textContent = String(Math.round(target * (1 - Math.pow(1 - p, 3))));
        if (p < 1) requestAnimationFrame(tick);
        else el.textContent = el.getAttribute('data-pw-count');
      })(t0);
    };
    if (typeof inView === 'function') inView(el, function () { fire(); return function () {}; });
    else fire();
  });

  /* ---------- 4. scroll progress bar + header shrink + parallax ---------- */
  var bar = document.createElement('div');
  bar.id = 'pw-progress';
  bar.setAttribute('aria-hidden', 'true');
  document.body.appendChild(bar);
  var header = document.querySelector('.pw-header');
  var parallax = Array.prototype.filter.call(
    document.querySelectorAll('.pw-hero-editorial img, .pw-hero-grid img'),
    allowed
  );
  var ticking = false;
  var lastSweep = 0;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      ticking = false;
      var now = performance.now();
      var max = document.documentElement.scrollHeight - window.innerHeight;
      var p = max > 0 ? Math.min(1, window.scrollY / max) : 0;
      bar.style.transform = 'scaleX(' + p + ')';
      bar.style.opacity = p > 0.005 ? '1' : '0';
      if (header) header.classList.toggle('pw-scrolled', window.scrollY > 40);
      /* force-show any reveal the user has already scrolled past */
      if (now - lastSweep > 400) { lastSweep = now; sweep(); }
      parallax.forEach(function (el) {
        var r = el.getBoundingClientRect();
        var mid = r.top + r.height / 2 - window.innerHeight / 2;
        el.style.transform = 'translateY(' + Math.max(-26, Math.min(26, -mid * 0.05)) + 'px)';
      });
    });
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  /* ---------- 5. magnetic buttons (pointer devices only) ---------- */
  if (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    Array.prototype.forEach.call(document.querySelectorAll('.pw-button, .btn-theme, .pw-actions a'), function (btn) {
      if (btn.closest(SKIP)) return;
      btn.addEventListener('mousemove', function (ev) {
        var r = btn.getBoundingClientRect();
        var dx = (ev.clientX - r.left - r.width / 2) / r.width;
        var dy = (ev.clientY - r.top - r.height / 2) / r.height;
        animate(btn, { x: dx * 10, y: dy * 6, scale: 1.03 }, { duration: 0.35, easing: 'ease-out' });
      });
      btn.addEventListener('mouseleave', function () {
        animate(btn, { x: 0, y: 0, scale: 1 }, { duration: 0.5, easing: [0.22, 1, 0.36, 1] });
      });
    });
  }

  /* ---------- 6. gallery image crossfade on src change ---------- */
  var gal = document.querySelector('.my-gallery img, .photos__item--main img');
  if (gal && window.MutationObserver) {
    new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        if (m.attributeName === 'src') {
          animate(gal, { opacity: [0, 1], scale: [1.03, 1] }, { duration: 0.55, easing: [0.22, 1, 0.36, 1] });
        }
      });
    }).observe(gal, { attributes: true, attributeFilter: ['src'] });
  }

  /* ---------- failsafes & lifecycle ---------- */
  window.setTimeout(function () {
    sweep();
    Array.prototype.forEach.call(document.querySelectorAll('.pw-hero, .pw-hero--page, .pw-hero--collection'), function (el) {
      Array.prototype.forEach.call(el.querySelectorAll('*'), function (n) {
        var cs = getComputedStyle(n);
        if (parseFloat(cs.opacity) < 0.05) forceShow(n);
      });
    });
  }, 2800);
  window.setTimeout(sweep, 4500);
  window.setTimeout(sweep, 7000);
  window.setTimeout(showAll, 9000);
  document.addEventListener('visibilitychange', function () { if (document.visibilityState !== 'visible') showAll(); });
  window.addEventListener('blur', showAll);
  window.addEventListener('resize', showAll, { passive: true });
  if (window.matchMedia) {
    var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.addEventListener) mq.addEventListener('change', showAll);
  }
})();
